import React, { useState } from "react";
import { Path, useFieldArray, useForm } from "react-hook-form";
import DriverDto from "../Common/dtos/DriverDto";
import { FormTextControl, RowFormText } from "../Common/Forms/FormText";
import KaAlert from "../Common/KaAlert";
import EntityModal, { ModalMode } from "../Common/EntityModal";
import useDisplayMessage from "../Common/useDisplayMessage";
import { Col, FormGroup } from "reactstrap";
import { FormLabel } from "../Common/Forms/FormLabel";
import BoldButton from "../Common/BoldButton";
import useUpsertDriver, { DriverUpsertDto } from "../../requests/useUpsertDriver";

interface CreateEditDriverProps {
    initialDriver: DriverDto | null;
    onDismiss: () => void;
    onUpsert: (driverId: string, driverName: string, assignSites: boolean) => void;
    mode: ModalMode;
}

type DriverForm = Omit<DriverUpsertDto, 'emailAddresses'> & {
  emailAddresses: { email: string }[]
}

export const CreateEditDriver = (props: CreateEditDriverProps) => {
  const displayMessage = useDisplayMessage();
  const { id, emailAddresses: initialEmails, ...initialDriver } = { ...props.initialDriver };

  const initEmails = initialEmails?.map(email => ({ email })) ?? [];
  const { register, handleSubmit, control, getValues, setValue, trigger, formState: { errors } } = useForm<DriverForm>({
      defaultValues: {
          ...initialDriver,
          emailAddresses: initEmails.length === 0 ? [{ email: '' }] : initEmails,
      }
  });
  const emailAddresses = useFieldArray({ control, name: 'emailAddresses' });

    const validateUniqueEmail = (value: string) => {
        if (value && getValues('emailAddresses').map(f => f.email).filter(e => e === value).length > 1) {
            return 'Email already exists';
        }
        return true;
    };

    const removeEmail = (index: number) => {
        emailAddresses.remove(index);
        trigger('emailAddresses');
    }

    const [assignSites, setAssignSites] = useState(false);

    const onClose = () => {
        props.onDismiss();
        displayMessage.clear();
    };

    const upsert = useUpsertDriver(props.initialDriver?.id, {
        onSuccess: (response) => {
            onClose();
            const driverId = props.initialDriver ? props.initialDriver.id : response;
            props.onUpsert(driverId, getValues().name, assignSites);
        },
        onError: (e) => displayMessage.fail(e.message),
    });

    const submit = (formValue: DriverForm, assign: boolean) => {
        setAssignSites(assign);
        upsert.request({
            ...formValue,
            emailAddresses: formValue.emailAddresses.map(e => e.email).filter(e => !!e),
        });
    }

    const hookProps = (name: Path<DriverForm>) => ({
        labelSpace: 4,
        inputSpace: 7,
        name: name,
        setValue,
        register,
        errors,
    });

    const labelSpace = 4;
    const inputSpace = 7;

    const emailControl = (index: number) =>
        <FormTextControl
            {...hookProps(`emailAddresses.${index}.email`)}
            displayName='Email address'
            options={{
                onChange: () => trigger('emailAddresses'),
                validate: validateUniqueEmail,
                pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Email Address is invalid'
                }
            }} />

    return (<EntityModal
      mode={props.mode}
      entityName="driver"
      onHide={onClose}
      isSaving={upsert.isLoading && !upsert.isError}
      onSubmit={handleSubmit((formValue) => submit(formValue, false))}
      onSubmitAndAssign={handleSubmit((formValue) => submit(formValue, true))}>
          <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
          <RowFormText {...hookProps('name')}
              options={{
                  required: 'Name is required',
                  validate: { length: v => v.length < 51 || 'Name cannot be longer than 50 characters' }
              }} />
          <RowFormText {...hookProps('number')}
              options={{
                  validate: { length: v => v.length < 51 || 'Number cannot be longer than 50 characters' }
              }} />

          <RowFormText {...hookProps('password')}
              options={{
                  validate: { length: v => v.length < 51 || 'Password cannot be longer than 50 characters' }
              }} />
          <RowFormText {...hookProps("addressStreet")}
            options={{
              validate: {
                length: v => v.length < 51 || 'Address Street cannot be longer than 50 characters' }
            }} />
          <RowFormText {...hookProps("addressCity")}
            options={{
              validate: {
                length: v => v.length < 51 || 'Address City cannot be longer than 50 characters' }
            }} />
          <RowFormText {...hookProps("addressState")}
            options={{
              validate: {
                length: v => v.length < 51 || 'Address State cannot be longer than 50 characters' }
            }} />
          <RowFormText {...hookProps("addressPostalCode")}
            options={{
              validate: {
                length: v => v.length < 16 || 'Address Postal Code cannot be longer than 15 characters' }
            }} />
          <RowFormText {...hookProps("addressCountry")}
            options={{
              validate: {
                length: v => v.length < 101 || 'Address Country cannot be longer than 100 characters' }
            }} />
          <RowFormText {...hookProps('phone')}
              options={{
                  validate: { length: v => v.length < 51 || 'Phone cannot be longer than 50 characters' }
              }} />
          {
            emailAddresses.fields.map((field, index) => {
                const isFirst = index === 0;
                const showX = emailAddresses.fields.length > 1;
                return <FormGroup row key={field.id}>
                    {isFirst &&
                        <Col xs={{ size: labelSpace }}>
                            <FormLabel displayName='Email' {...hookProps(`emailAddresses.${index}.email`)} />
                        </Col>
                    }
                    <Col xs={{ offset: (isFirst ? 0 : labelSpace), size: inputSpace - (showX ? 1 : 0) }}>
                        {emailControl(index)}
                    </Col>
                    {
                        showX && props.mode !== ModalMode.View &&
                        <Col xs="1" className="pe-0">
                            <BoldButton className='btn-close btn-sm mt-2' onClick={() => removeEmail(index)} />
                        </Col>
                    }
                </FormGroup>
              })}
          <FormGroup row>
              <Col xs={{ offset: labelSpace, size: inputSpace }} className='text-end'>
                  <BoldButton
                      className='col-auto btn-ghost-primary'
                      disabled={emailAddresses.fields.length >= 10}
                      onClick={() => emailAddresses.append({ email: '' })}>
                      Add Email
                  </BoldButton>
              </Col>
          </FormGroup>
      </EntityModal>);
};
